import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IWspUiEnv} from "/@lib@/wsp/wsp.js";
import {Area, IArea, SimpleTagArea} from "/@lib@/commons/areas.js";
import {Button, ButtonActions, OButtonActionsInit} from "/@back@/commons/widgets/buttons.js";
import {ACTION, Action, IAction, IActionToggle} from "/@lib@/commons/actions.js";
import {EOperatorsSimple, ISearchCrit, SearchAnd, SearchFree, SearchFullXPath, SearchItemLinks, SearchNot, SearchOccurencesSubExp, SearchOr, SearchRegexpUri} from "/@lib@/wsp/search.js";
import {LANG} from "/@lib@/commons/lang.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {JWedSearchBarParams} from "/@back@/edit/wed/features/searchBar.js";
import {IEndPoint} from "/@lib@/commons/io/io.js";

/**
 * Factory des widgets d'affichage des critères de recherche.
 * Par défaut, la liste des critères disponibles est issue de la liste d'extPoints :
 * - "wsp.crit.items" pour les recherches d'items.
 * - "wsp.crit.tasks" pour les recherches de tâches.
 */
export interface IUiCritArea extends IArea<OUiCritInit, IUiCritWidget> {

	/** Signature du critère pour gérer les alternatives autorisées. */
	critSgn?: string

	/** Evalue si un critère issu de cette factory pourrait remplacer le critère instancié passé en paramètre. */
	canReplace(uiCrit: IUiCritWidget, ctx: OUiCritInit): boolean

	/** Construit un résumé HTML textuel (enrichi d'éventuels tooltips) de ce critère de recherche. */
	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined

	/** Construit des critère de recherches Wed internes à l'item pour poursuivre la requete dans l'item. */
	buildWedSearch?(xml: Element, criterions: Dict<IUiCritArea>): JWedSearchBarParams | undefined
}

/**
 *
 */
export interface IUiCritWidget extends BaseElement {

	critArea: IUiCritArea

	initialize(init: OUiCritInit): this;

	/** Initialisation du critère à partir de sa forme xml. Doit être appelé après initialize(init). */
	initFromXmlCrit(xml: Element): Promise<this>

	/**
	 * Initialise ce critère lorsqu'il est issu d'une action d'insertion / remplacement de l'utilisateur.
	 * Permet d'enclencher l'ouverture auto d'un popup par ex.
	 * La méthode this.dispatchChangeEvent() est automatiquement appelée par cette méthode.
	 * Cette méthode doit être appelé après initialize(init).
	 */
	initFromInsAction(xml?: Element): Promise<this>

	buildCrit(): ISearchCrit | undefined

	buildUiXmlCrit(): Element | undefined

	/** Dispatch un event 'wsp-uicrit-change' signalant un changement. */
	dispatchChangeEvent(): void
}

export function IS_UiCritWidget(n: any): n is IUiCritWidget {return n instanceof HTMLElement && ('buildCrit' in n)}

/**
 * Recherche d'un constructeur de widget de critère à partir de sa représentation xml.
 */
export function buildCritAreaFromXml(elt: Element, criterions: Dict<IUiCritArea>): IUiCritArea | undefined {
	if (!elt) return undefined;
	const key = elt.getAttribute('uiCrit') || elt.localName;
	return (criterions ? criterions[key] : null) || UiCritUnknown.AREA;
}


/** Impl de base de IUiCritArea */
export class UiCritArea extends SimpleTagArea<OUiCritInit, IUiCritWidget> implements IUiCritArea {

	/** Pattern de regroupement des critères remplaçables entre eux. */
	replacePattern?: RegExp;

	critSgn?: string;

	buildBody(ctx: OUiCritInit): IUiCritWidget {
		const w = super.buildBody(ctx);
		w.critArea = this;
		return w;
	}

	canReplace(uiCrit: IUiCritWidget, ctx: OUiCritInit): boolean {
		if (!this.replacePattern || !uiCrit.critArea.critSgn) return false;
		return this.replacePattern.test(uiCrit.critArea.critSgn);
	}

	setCritSgn(critSgn: string): this {
		this.critSgn = critSgn;
		return this;
	}

	setReplacePattern(replacePattern: RegExp): this {
		this.replacePattern = replacePattern;
		return this;
	}

	setCanReplace(fct: (uiCrit: IUiCritWidget, ctx: OUiCritInit) => boolean): this {
		this.canReplace = fct;
		return this;
	}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		return <span class="crit">{this.getLabel(ctx)}</span>;
	}

	setBuildAbstract(fct: (xml: Element, ctx: OUiCritInit, options?: any) => HTMLElement | undefined): this {
		this.buildAbstract = fct;
		return this;
	}

	buildWedSearch(xml: Element, criterions: Dict<IUiCritArea>): JWedSearchBarParams | undefined {return undefined}

	setBuildWedSearch(fct: (xml: Element, criterions: Dict<IUiCritArea>) => JWedSearchBarParams): this {
		this.buildWedSearch = fct;
		return this;
	}
}

export interface OUiCritInit extends OSkinableInit {
	reg: IReg<IWspUiEnv>

	/**
	 * Map des critères disponibles pour ce contexte de recherche. La key de la map est l'id de l'Area
	 * qui doit être la valeur de l'attribut "uiCrit" de la forme sérialisée en XML du critère.
	 * Par défaut, la liste des critères disponibles est issue de la liste d'extPoints :
	 * - "wsp.crit.items" pour les recherches d'items.
	 * - "wsp.crit.tasks" pour les recherches de tâches.
	 */
	criterions?: Dict<IUiCritArea>;

	hideAlternativesBtn?: boolean
}

export interface OUiCritWithInputInit {
	placeholder?: string
}

/**
 * Widget de base pour l'affichage d'un critère de recherche.
 * Doit émettre un event "wsp-uicrit-change" à chaque changement effectif du critère.
 */
export abstract class UiCritBase extends BaseElement implements IUiCritWidget {

	/** Factory de ce widget*/
	critArea: IUiCritArea;

	reg: IReg<IWspUiEnv>;

	criterions: Dict<IUiCritArea>;

	/** this.dispatchChangeEvent.bind(this) */
	protected onChange: () => void;

	protected _initialize(init: OUiCritInit): void {
		if (!this.criterions) this.criterions = init.criterions;
		this.reg = this.findReg(init);
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin('wsp-uicrit', this.shadowRoot);
		this._initAndInstallSkin(this.localName, init);
		this.onChange = this.dispatchChangeEvent.bind(this);
	}

	static initHeadFromArea(critArea: IUiCritArea, ctx: OUiCritInit, headElt: HTMLElement) {
		if (critArea && headElt) {
			const desc = critArea.getDescription(ctx);
			if (desc) headElt.title = desc;
		}
	}

	buildInitFromAtts(init?: OUiCritInit): any {
		init = super.buildInitFromAtts(init);
		if (!init.criterions) {
			const criterionsHolder = DOMSH.findParentElt(this, null, (n: any): n is Element => (n as OUiCritInit).criterions != null) as any as OUiCritInit;
			if (criterionsHolder) init.criterions = criterionsHolder.criterions;
		}
		return init;
	}

	abstract initFromXmlCrit(xml: Element): Promise<this>;

	async initFromInsAction(xml?: Element): Promise<this> {
		if (xml) await this.initFromXmlCrit(xml);
		this.dispatchChangeEvent();
		return this;
	}

	abstract buildCrit(): ISearchCrit | undefined;

	buildUiXmlCrit(): Element | undefined {
		const crit = this.buildCrit();
		if (!crit) return undefined;
		const elt = crit.toDom();
		if (elt) elt.setAttribute("uiCrit", this.getUiCritXmlAtt());
		return elt;
	}

	/** Valeur de l'attribut XML 'uiCrit' utilisé pour sérialiser ce critère en XML. */
	protected getUiCritXmlAtt(): string {
		return this.localName;
	}

	dispatchChangeEvent() {
		this.dispatchEvent(new CustomEvent('wsp-uicrit-change', {bubbles: true}));
	}

	protected insertAlternativesBtn(init: OUiCritInit): ButtonActions<OUiCritInit> | null {
		if (init.hideAlternativesBtn) return null;

		class CritAction extends Action<OUiCritInit> {
			constructor(public area: IUiCritArea, public replace: UiCritBase) {
				super(area.getId());
			}

			getLabel(ctx: OUiCritInit): string {return this.area.getLabel(ctx)}

			async execute(ctx: OUiCritInit, ev?: Event) {
				const elt = (await this.area.loadBody(ctx)).initialize(ctx);
				const currentCritXml = this.replace.buildUiXmlCrit();
				this.replace.parentNode.insertBefore(elt, this.replace);
				this.replace.remove();
				elt.initFromInsAction(currentCritXml);
			}
		}

		const actions = [] as IAction<OUiCritInit>[];
		if (init.criterions) for (const area of Object.values(init.criterions)) {
			if (area !== this.critArea && area.canReplace(this, init) && area.isVisible(this)) actions.push(new CritAction(area, this));
		}
		return actions.length > 0 ? this.buildAlternativesBtn(actions, this.findReg(init)) : null;
	}

	protected buildAlternativesBtn(actions: IAction<OUiCritInit>[], reg: IReg<IWspUiEnv>): ButtonActions<OUiCritInit> | null {
		return <ButtonActions class="altBtn" î={{
			reg: reg,
			label: "⇄",
			actions: actions,
			actionContext: this
		}}/> as ButtonActions<OUiCritInit>;
	}

}

/** Stylage commun à tous les widgets de définition de crtitères. */
REG.reg.registerSkin('wsp-uicrit', 1, /* language=CSS */ `
	:host {
		display: block;
		padding: 2px;
		overflow: hidden;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	.critHead {
		display: flex;
		min-height: 0;
		min-width: 0;
		user-select: none;
		align-items: center;
	}

	.critTitle {
		font-weight: bold;
		margin: 2px;
	}

	c-button-actions {
		min-width: fit-content;
	}

	.h {
	  flex: 1;
	  display: flex;
	  min-height: 0;
	  min-width: 0;
	  flex-direction: row;
	  align-items: center;
  }

  input[type=search] {
	  flex: 1;
	  min-width: 0;
	  min-height: 0;
  }

  .inputsCtn {
	  margin-inline-start: 1em;
  }

  input, select {
	  margin-inline-start: 1em;
	  background-color: var(--form-bgcolor);
	  color: var(--form-color);
		border: 1px solid var(--border-color);
		font-size: inherit;
	}

	.opt {
		font-size: var(--label-size);
		display: flex;
		align-items: center;
		user-select: none;
	}

	:invalid {
		border-color: var(--error-color);
		color: var(--error-color);
	}

	.altBtn {
		display: inline-flex;
		background: var(--insbtn-bg);
		color: var(--insbtn-color);
		border: 1px outset var(--border-color);
		border-radius: 3px;
		margin: 0 2px;
		padding: 2px;
	}

	.altBtn:hover {
		background: var(--insbtn-hover-bg);
	}
`);

/** Area pour les opérateurs booléen AND / OR intégrant le sous opérateur NOT optionnel pour chaque fils. */
class UiCritBoolArea extends Area<OUiCritInit, IUiCritWidget> implements IUiCritArea {

	constructor(id: 'and' | 'or', public hideCritHeader?: boolean) {
		super(id);
		if (!hideCritHeader) this._group = "bool";
	}

	getLabel(ctx: OUiCritInit): string {return this._id === 'or' ? "Groupe de critères (... OU ...)" : "Groupe de critères (... ET ...)"}

	buildBody(ctx: OUiCritInit): IUiCritWidget {
		const w = new UiCritBool();
		w.critArea = this;
		return w;
	}

	canReplace(uiCrit: IUiCritWidget, ctx: OUiCritInit): boolean {return uiCrit instanceof UiCritBool}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		const liaise = this._id === 'or' ? " OU " : " ET ";
		const liaiseNot = this._id === 'or' ? " OU SAUF " : " ET SAUF ";
		const chAbstract = [] as HTMLElement[];
		for (let ch = xml.firstElementChild; ch; ch = ch.nextElementSibling) {
			const isNot = ch.localName === 'not';
			const chXml = isNot ? ch.firstElementChild : ch;
			const chCrit = buildCritAreaFromXml(chXml, ctx.criterions);
			const sub = chCrit ? chCrit.buildAbstract(chXml, ctx) : <span class="critUnknown"><span class="xml">{DOM.ser(chXml)}</span></span>;
			if (sub) {
				if (chAbstract.length > 0) chAbstract.push(<span class="boolOp">{isNot ? liaiseNot : liaise}</span>);
				else if (isNot) chAbstract.push(<span class="boolOp"> SAUF </span>);
				chAbstract.push(sub);
			}
		}
		if (chAbstract.length === 0) return undefined;
		if (this.hideCritHeader) return <span>{chAbstract}</span>;
		return <span class="boolGroup">
			<span class="boolBracket">(</span>
			{chAbstract}
			<span class="boolBracket">)</span>
			</span>;
	}

	buildWedSearch(xml: Element, criterions: Dict<IUiCritArea>): JWedSearchBarParams {
		for (let ch = xml.firstElementChild; ch; ch = ch.nextElementSibling) {
			const chCrit = buildCritAreaFromXml(ch, criterions);
			if (chCrit && ("buildWedSearch" in chCrit)) {
				const s = chCrit.buildWedSearch(ch, criterions);
				if (s) return s;
			}
		}
	}
}

/**
 * Widgets pour les opérateurs booléens AND et OR.
 */
export class UiCritBool extends UiCritBase {

	static AREA_AND = new UiCritBoolArea('and');
	static AREA_OR = new UiCritBoolArea('or');
	static AREA_AND_ROOT = new UiCritBoolArea('and', true);
	static AREA_OR_ROOT = new UiCritBoolArea('or', true);

	critArea: UiCritBoolArea;

	get booleanOp(): 'and' | 'or' {return this.critArea.getId() as 'and' | 'or'};

	label: HTMLElement;

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		if (!this.critArea.hideCritHeader) {
			const head = this.shadowRoot.appendChild(<div id="critHead"/>);
			this.label = head.appendChild(<span/>);
			head.appendChild(this.buildAlternativesBtn([new Action<OUiCritInit>('altBool')
				.setLabel((ctx: OUiCritInit) => {
					return this.critArea === UiCritBool.AREA_AND ? UiCritBool.AREA_OR.getLabel(ctx) : UiCritBool.AREA_AND.getLabel(ctx);
				})
				.setExecute(() => {
					this.critArea = this.critArea === UiCritBool.AREA_AND ? UiCritBool.AREA_OR : UiCritBool.AREA_AND;
					this._refresh();
				})], this.reg));
		}
		this.shadowRoot.appendChild(<slot id="critCh"/>);
		this.addEventListener("wsp-uicrit-toDelete", (ev: CustomEvent) => {
			const ch = ev.target as Node;
			if (ch && ch.parentElement === this) this.deleteCrit(ch as IUiCritWidget);
			ev.stopImmediatePropagation();
		})
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		try {
			this.freezeRefresh();
			for (let ch = xml.firstElementChild; ch; ch = ch.nextElementSibling) {
				const isNot = ch.localName === 'not';
				const critXml = isNot ? ch.firstElementChild : ch;
				const critArea = buildCritAreaFromXml(critXml, this.criterions);
				if (critArea) {
					const crit = await critArea.loadBody(this);
					if (crit) {
						crit.initialize(this);
						await crit.initFromXmlCrit(critXml);
						this.insertCrit(crit, isNot);
					}
				}
			}
		} finally {
			this.unfreezeRefresh();
		}
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		const crit = this.booleanOp === 'or' ? new SearchOr() : new SearchAnd();
		for (let ch = DOM.findFirstChild(this, IS_UiCritWidget); ch; ch = DOM.findNextSibling(ch, IS_UiCritWidget)) {
			LANG.pushIfDefined(crit, ch.buildCrit());
		}
		return crit.length > 0 ? crit : undefined;
	}

	buildUiXmlCrit(): Element | undefined {
		return JSX.asXml(() => {
			const root = this.booleanOp === 'or' ? <or/> : <and/>;
			if (!this.critArea.hideCritHeader) root.setAttribute("uiCrit", this.booleanOp === 'or' ? "wsp-uicrit-bool:or" : "wsp-uicrit-bool:and");
			for (let ch = DOM.findFirstChild(this, IS_UiCritWidget); ch; ch = DOM.findNextSibling(ch, IS_UiCritWidget)) {
				const sub = ch.buildUiXmlCrit();
				if (sub) root.appendChild(sub);
			}
			return root; //NON root.hasChildNodes() ? root : undefined; Sinon mauvaise reconstruction graphique.
		});
	}

	insertCrit(crit: IUiCritWidget, notOp?: boolean, insertBefore?: HTMLElement) {
		const notOpt = new UiCritNotOptional().initialize(this).initFromContext(this.booleanOp, notOp);
		//TODO notOpt.setAttribute("draggable", "true");
		notOpt.appendChild(crit);
		this.insertBefore(notOpt, insertBefore);
		crit.initFromInsAction();
		this.refresh();
	}

	deleteCrit(crit: IUiCritWidget) {
		crit.remove();
		this.dispatchChangeEvent();
		this.refresh();
	}

	_refresh() {
		const op = this.booleanOp;
		if (this.label) DOM.setTextContent(this.label, op === 'and' ? "Tous les sous-critères sont remplis" : "Au moins un des sous-critères est rempli");
		let insFound = false;
		for (let ch = this.firstElementChild; ch;) {
			const next = ch.nextElementSibling;
			if (ch instanceof Button) {
				if (insFound) ch.remove();
				else insFound = true;
			} else if (ch instanceof UiCritNotOptional) {
				ch.booleanContext = op;
				if (!insFound) this._insertAddBtn(ch);
				else insFound = false;
			}
			ch = next;
		}
		if (!insFound) this._insertAddBtn(null);
	}

	protected _insertAddBtn(before: Element) {
		this.insertBefore(<Button class="insCrit" skin="wsp-uicrit-bool/insCrit" label="+" title="Insérer un critère" onclick={this.onInsert}/>, before);
	}

	protected onInsert(this: Button, ev: MouseEvent) {

		class CritAction extends Action<Button> {
			constructor(public critArea: IUiCritArea) {
				super(critArea.getId());
			}

			getGroup(ctx: Button): string {
				return this.critArea.getGroup(ctx.parentElement as UiCritBool);
			}

			getLabel(ctx: Button): string {
				return this.critArea.getLabel(ctx.parentElement as UiCritBool);
			}

			getIcon(ctx: Button): string {
				return this.critArea.getIcon(ctx.parentElement as UiCritBool);
			}

			getDescription(ctx: Button): string {
				return this.critArea.getDescription(ctx.parentElement as UiCritBool);
			}

			isVisible(ctx: Button): boolean {return this.critArea.isVisible(ctx.parentElement as UiCritBool)}

			isEnabled(ctx: Button): boolean {return this.critArea.isEnabled(ctx.parentElement as UiCritBool)}

			async execute(ctx: Button, ev?: Event) {
				const critCtn = ctx.parentElement as UiCritBool;
				critCtn.insertCrit(await this.critArea.loadBody(critCtn), false, ctx);
			}
		}

		const critCtn = this.parentElement as UiCritBool;
		let crits = Object.values(critCtn.criterions)
			.filter((area) => area.getGroup(critCtn))
			.map((area) => new CritAction(area)) as IAction<Button>[];
		crits = ACTION.injectSepByGroup(crits, "props bool scope * advanced", this);

		POPUP.showPopupActionsFromEvent({
			actions: crits,
			actionContext: this
		}, ev);
	}

}


REG.reg.registerSkin('wsp-uicrit-bool', 1, /* language=CSS */ `
	#critCh {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		margin: .5em 2px;
	}
`);

REG.reg.registerSkin('wsp-uicrit-bool/insCrit', 1, /* language=CSS */ `
	:host {
		color: var(--insbtn-color);
		background-color: var(--bgcolor);
		align-self: start;
		display: flex;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		border: 1px outset var(--border-color);
		border-radius: 3px;
		cursor: pointer;
		height: 1.2em;
		width: 1.2em;
		margin: -.6em 0;
		padding: 0 2px;
		z-index: 1;
		user-select: none;
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}

	.label {
		flex: 1;
		display: block;
		background: var(--insbtn-bg);
		line-height: 1.2em;
		min-width: 1.2em;
		text-align: center;
	}

	:host(:hover) {
		background-color: var(--insbtn-hover-bg);
	}
`);
customElements.define("wsp-uicrit-bool", UiCritBool);


/**
 * Container d'un et d'un seul 'vrai' IUiCritWidget destiné à gérer le critère optionnel not() pour chaque
 * fils d'un IUiCritWidget AND ou OR.
 */
export class UiCritNotOptional extends UiCritBase {

	public popupBtn: ButtonActions<UiCritNotOptional>;

	protected _booleanContext: 'and' | 'or';
	protected _isNot: boolean;

	get booleanContext(): 'and' | 'or' {return this._booleanContext;}

	set booleanContext(v: 'and' | 'or') {
		if (this._booleanContext !== v) {
			this._booleanContext = v;
			this._refresh();
		}
	}

	get isNot(): boolean {return this._isNot}

	set isNot(v: boolean) {
		if (this._isNot !== v) {
			this._isNot = v;
			this._refresh();
			this.dispatchChangeEvent();
		}
	}

	getChildCrit(): IUiCritWidget | null {return DOM.findFirstChild(this, IS_UiCritWidget)}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);

		class NotAction extends Action<UiCritNotOptional> implements IActionToggle<UiCritNotOptional> {
			constructor(public isNot: boolean, public crit: UiCritNotOptional) {
				super(isNot ? 'not' : '');
				if (isNot) {
					this._label = function (ctx: UiCritNotOptional) {return ctx.booleanContext === 'or' ? "OU SAUF" : "ET SAUF"};
				} else {
					this._label = function (ctx: UiCritNotOptional) {return ctx.booleanContext === 'or' ? "OU" : "ET"};
				}
			}

			isToggle(ctx: UiCritNotOptional): this is IActionToggle<UiCritNotOptional> {return true}

			/** null pour l'état 'mixed'. */
			getDatas(api: 'toggle', ctx: UiCritNotOptional): boolean | null {return this.isNot === ctx.isNot}

			isEnabled(ctx: UiCritNotOptional): boolean {
				if (this.isNot === ctx.isNot) return false;
				return super.isEnabled(ctx);
			}

			execute(ctx: UiCritNotOptional, ev?: Event): any | "noPreventDefault" {
				this.crit.isNot = this.isNot;
			}
		}

		this.popupBtn = this.shadowRoot.appendChild(<ButtonActions î={{
			actionContext: this,
			actions: [new NotAction(false, this), new NotAction(true, this)]
		} as OButtonActionsInit<UiCritNotOptional>}/> as ButtonActions<UiCritNotOptional>);
		this.shadowRoot.appendChild(<slot/>);
		this.shadowRoot.appendChild(<Button id="del" title="Supprimer" icon="/@skin@/commons/icons/delete.svg" onclick={this.onDelete}/> as Button);
	}

	initFromContext(booleanContext: 'and' | 'or', isNot: boolean): this {
		this._booleanContext = booleanContext;
		this._isNot = isNot;
		this.refresh();
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		const ch = this.getChildCrit();
		if (!ch) return undefined;
		const crit = ch.buildCrit();
		if (!crit) return undefined;
		return this._isNot ? new SearchNot(crit) : crit;
	}

	buildUiXmlCrit(): Element | undefined {
		const ch = this.getChildCrit();
		if (!ch) return undefined;
		const crit = ch.buildUiXmlCrit();
		return crit && this._isNot ? <not>{crit}</not> : crit;
	}

	protected onDelete(this: Button, ev: MouseEvent) {
		DOMSH.findHost(this).dispatchEvent(new CustomEvent("wsp-uicrit-toDelete", {bubbles: true, composed: true}));
	}

	protected _refresh() {
		this.popupBtn.label = this.isNot ? this.booleanContext === 'or' ? "OU SAUF" : "ET SAUF" : this.booleanContext === 'or' ? "OU" : "ET";
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		//NOT USED.
		return this;
	}
}

/** Stylage commun à tous les widgets de définition de crtitères. */
REG.reg.registerSkin('wsp-uicrit-notopt', 1, /* language=CSS */ `
	:host {
		display: flex;
		flex-direction: row;
		border: 1px solid var(--border-color);
		margin-bottom: -1px;
	}

	::slotted(*) {
		flex: 1;
	}
`);

customElements.define("wsp-uicrit-notopt", UiCritNotOptional);


/**
 * UiCritFree
 * @uiLabel : label libre de cette condition exploité dans l'abstract
 */
export class UiCritFree extends UiCritBase {

	static AREA = new UiCritArea('wsp-uicrit-free').setLabel("Condition XML avancée").setGroup("advanced").setBuildAbstract((xml: Element, ctx: OUiCritInit): HTMLElement => {
		const label = xml.getAttribute("uiLabel");
		return label ? <span class="crit"><span class="critVal">{label}</span></span> : <span class="crit">Condition avancée</span>;
	});

	input: HTMLTextAreaElement;

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		this.reg.installSkin('wsp-uicrit-free', sr);
		sr.appendChild(<div id="critHead">
			<span>Condition XML avancée</span>
			{this.insertAlternativesBtn(init)}
		</div>);
		this.input = sr.appendChild(<textarea onblur={this.onBlur} oninput={this.onInput}/>) as HTMLTextAreaElement;
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		this.input.value = DOM.ser(xml);
		this.validateInput();
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		if (!this.input.value) return undefined;
		const dom = DOM.parseDomValid(this.input.value);
		if (!dom) return undefined;
		return new SearchFree(dom.documentElement);
	}

	validateInput(): boolean {
		const txt = this.input.value;
		const dom = txt ? DOM.parseDomValid(txt) : null;
		this.input.setCustomValidity(dom ? "" : "Cette condition XML n'est pas syntaxiquement correcte");
		return this.input.reportValidity();
	}

	_dirty: boolean;

	onInput(this: HTMLTextAreaElement) {
		const me = DOMSH.findHost<UiCritFree>(this);
		me._dirty = true;
	}

	onBlur(this: HTMLTextAreaElement) {
		const me = DOMSH.findHost<UiCritFree>(this);
		if (me._dirty) {
			me._dirty = false;
			if (me.validateInput()) me.onChange();
		}
	}

}

customElements.define("wsp-uicrit-free", UiCritFree);

REG.reg.registerSkin('wsp-uicrit-free', 1, /* language=CSS */ `
	textarea {
		width: -webkit-fill-available;
		height: -webkit-fill-available;
	}
`);

/**
 * UiCritUnknown
 */
export class UiCritUnknown extends UiCritFree {

	static AREA = new UiCritArea('wsp-uicrit-unknown').setLabel("Condition inconnue");

	buildUiXmlCrit(): Element | undefined {
		const crit = this.buildCrit();
		if (!crit) return undefined;
		return crit.toDom();
	}
}

customElements.define("wsp-uicrit-unknown", UiCritUnknown);

/**
 * UiCritSrcUri
 */
export class UiCritSrcUri extends UiCritBase {

	static AREA = new UiCritArea("wsp-uicrit-srcuri-regexp").setLabel("Chemin de l'item")
		.setCritSgn("string").setReplacePattern(/\bstring\b/).setGroup("advanced").setBuildAbstract((xml: Element, ctx: OUiCritInit): HTMLElement => {
			const searchValue = xml.getAttribute("uiPathSearch") || "";
			const searchTxt = `Chemin de l'item contient "${searchValue}"`;
			return <span class="crit"><span class="critVal">{searchTxt}</span></span>;
		});

	protected _textElt: HTMLInputElement;
	protected _regExpElt: HTMLInputElement;

	get isRegExp(): boolean {return this._regExpElt ? this._regExpElt.checked : false}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		this._textElt = sr.appendChild(<div class="critHead">
			<span class="critTitle">Chemin</span>
			<input type="search" oninput={this.onChange}/>
			{this.insertAlternativesBtn(init)}
		</div>).querySelector("input");
		const optsRoot = sr.appendChild(<div class="h"/>);
		this._regExpElt = optsRoot.appendChild(<label class="opt"><input type="checkbox" onclick={this.onChange}/>Expression régulière</label>).firstElementChild as HTMLInputElement;
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		if (xml.hasAttribute('uiPathSearch')) {
			// texte
			this._textElt.value = xml.getAttribute('uiPathSearch') || "";
			this._regExpElt.checked = false;
		} else {
			// regExp
			this._textElt.value = xml.getAttribute('regexp') || "";
			this._regExpElt.checked = true;
		}
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		const v = this._textElt.value;
		if (v && this.isRegExp) {
			return new SearchRegexpUri(new RegExp(v));
		} else if (v) {
			const regexp = new RegExp(".*" + v.replace(/\W/g, LANG.escapeChar4Regexp) + ".*", "i");
			return new SearchRegexpUri(regexp);
		} else
			return undefined;
	}

	buildUiXmlCrit(): Element | undefined {
		const crit: Element = super.buildUiXmlCrit();
		if (crit && !this.isRegExp) crit.setAttribute("uiPathSearch", this._textElt.value);
		return crit;
	}

}

customElements.define("wsp-uicrit-srcuri-regexp", UiCritSrcUri);


/**
 * UiCritXpath
 */
export class UiCritXpath extends UiCritBase {

	static AREA = new UiCritArea("wsp-uicrit-xpath").setLabel("XPath")
		.setCritSgn("string").setReplacePattern(/\bstring\b/).setGroup("advanced").setBuildAbstract((xml: Element, ctx: OUiCritInit): HTMLElement => {
			const searchTxt = <span class="critVal">{xml.getAttribute("xpathSearch") || ""}</span>;
			return <span class="crit">XPath : '{searchTxt}'</span>;
		});

	protected _xpathElt: HTMLInputElement;

	// key / value
	protected _Ns: Map<string, string> = new Map<string, string>();

	protected _DefaultNsCodeList: string;

	constructor(public nsListCode: string = "namespaces:defaultList") {
		super();
		this._DefaultNsCodeList = nsListCode;
	}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		this._xpathElt = sr.appendChild(<div class="critHead">
			<span class="critTitle">XPath</span>
			<input type="search" oninput={this.onInput} onblur={this.onBlur}/>
			{this.insertAlternativesBtn(init)}
		</div>).querySelector("input");

		// TODO : ajouter un composant UI d'édition des ns
		// Ajout des NS par défaut
		const nsList = this.reg.getListAsMap(this._DefaultNsCodeList) as Dict<string>;
		if (nsList) for (const prefix in nsList)
			this._Ns.set(prefix == "#default" ? "" : prefix, nsList[prefix]);

	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		this._xpathElt.value = xml.getAttribute('xpathSearch') || "";
		let ch = xml.firstElementChild;
		while (ch) {
			if (ch.localName == "ns")
				this._Ns.set(ch.getAttribute("prefix"), ch.getAttribute("uri"));
			ch = ch.nextElementSibling;
		}
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		const v = this._xpathElt.value;
		if (v)
			return new SearchFullXPath(v, this._Ns);
		return undefined;
	}

	_dirty: boolean;

	onInput(this: HTMLTextAreaElement) {
		const me = DOMSH.findHost<UiCritXpath>(this);
		me._dirty = true;
	}

	onBlur(this: HTMLTextAreaElement) {
		const me = DOMSH.findHost<UiCritXpath>(this);
		if (me._dirty) {
			me._dirty = false;
			me.onChange();
		}
	}

}

customElements.define("wsp-uicrit-xpath", UiCritXpath);

/**
 *  UiCritSubRequest
 */

/** Area pour les opérateurs booléen AND / OR intégrant le sous opérateur NOT optionnel pour chaque fils. */
class UiCritSubRequestArea extends UiCritArea implements IUiCritArea {

	constructor(id: 'desc' | 'asc' | 'parents' | 'children', public traitsCodeList: string | null = "linktraits:search:defaultList") {
		super(id);
	}

	getLabel(ctx: OUiCritInit): string {
		switch (this._id) {
		case "desc" :
			return "Sous-requête dans le réseau descendant de l'item courant";
		case "asc" :
			return "Sous-requête dans le réseau ascendant de l'item courant";
		case "children" :
			return "Sous-requête dans les items liés de l'item courant";
		default :
			return "Sous-requête dans les items liant l'item courant";
		}
	}

	getGroup(ctx: OUiCritInit): string {
		return "advanced";
	}

	buildBody(ctx: OUiCritInit): IUiCritWidget {
		const w = new UiCritSubRequest();
		w.critArea = this;
		return w;
	}

	canReplace(uiCrit: IUiCritWidget, ctx: OUiCritInit): boolean {return uiCrit instanceof UiCritSubRequest}

	buildAbstract(xml: Element, ctx: OUiCritInit, options?: any): HTMLElement | undefined {
		const operator = <span class="critVal">{UiCritSubRequest.OP[xml.getAttribute("op")][1]}</span>;
		const value = <span class="critVal">{xml.getAttribute("value")}</span>;

		const LinkExpXml = UiCritSubRequestArea.findSubExpLinksElement(xml);
		const subCritRootXml = LinkExpXml?.nextElementSibling;

		const subCritRoot = subCritRootXml ? buildCritAreaFromXml(subCritRootXml, ctx.criterions) : null;
		const subCritRootAbstract = subCritRoot?.buildAbstract(subCritRootXml, ctx);
		const linkTraitFilter = LinkExpXml?.getAttribute('linkTraitFilter') || "";
		let linkTraitFilterAbstract: string = null;
		if (linkTraitFilter) {
			const traits = ctx.reg.getListAsMap(this.traitsCodeList) as Dict<string> || {};
			linkTraitFilterAbstract = `filtre '${traits[linkTraitFilter] || linkTraitFilter}'`;
		}

		let areaShortLabel;
		switch (this._id) {
		case "desc" :
			areaShortLabel = <span>{linkTraitFilterAbstract ? `Nombre d'items descendants (${linkTraitFilterAbstract}) tels que :` : 'Nombre d\'items descendants tels que :'} {subCritRootAbstract} {operator} {value}</span>;
			break;
		case "asc" :
			areaShortLabel = <span>{linkTraitFilterAbstract ? `Nombre d'items ascendants (${linkTraitFilterAbstract}) tels que :` : 'Nombre d\'items ascendants tels que :'} {subCritRootAbstract} {operator} {value}</span>;
			break;
		case "children" :
			areaShortLabel = <span>{linkTraitFilterAbstract ? `Nombre d'items liés (${linkTraitFilterAbstract}) tels que :` : 'Nombre d\'items liés tels que :'} {subCritRootAbstract} {operator} {value}</span>;
			break;
		default :
			areaShortLabel = <span>{linkTraitFilterAbstract ? `Nombre d'items liants (${linkTraitFilterAbstract}) tels que :` : 'Nombre d\'items liants tels que :'} {subCritRootAbstract} {operator} {value}</span>;
		}
		return <span class="boolGroup">{areaShortLabel}</span>;
	}

	/** Retourne l'exp "exp[path="."]" dans les 2 contextes possibles :
	 * 	- exp[@type="OccurencesSubExp"]/and/exp[path="."]
	 * 	- OU exp[@type="OccurencesSubExp"]/exp[path="."]*/
	static findSubExpLinksElement(xml: Element): Element | null {
		if (xml?.firstElementChild?.localName === 'and')
			return xml.firstElementChild.firstElementChild?.getAttribute("path") === "." ?
				xml.firstElementChild.firstElementChild :
				null;
		else
			return xml?.firstElementChild?.getAttribute("path") === "." ?
				xml.firstElementChild :
				null;
	}
}


export class UiCritSubRequest extends UiCritBase {

	static AREA_DESC = new UiCritSubRequestArea("desc");

	static AREA_ASC = new UiCritSubRequestArea("asc");

	static AREA_CHILDREN = new UiCritSubRequestArea("children");

	static AREA_PARENTS = new UiCritSubRequestArea("parents");

	static OP: { [index: string]: any } = {
		//"value" : [longLabel, shortLabel]
		"gt": ["Items trouvés supérieur à", ">"],
		"gt=": ["Items trouvés supérieur ou égal à", ">="],
		"lt": ["Items trouvés inférieur à", "<"],
		"lt=": ["Items trouvés inférieur ou égal à", "<="],
		"=": ["Items trouvés égal à", "="],
	};

	critArea: UiCritSubRequestArea;

	_opElt: HTMLSelectElement;

	_opValueElt: HTMLInputElement;

	_subExpElt: UiCritBool;

	_trFilterElt: HTMLSelectElement | null;

	get traitsCodeList(): string | null {return this.critArea.traitsCodeList}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;

		const head = sr.appendChild(<div id="critHead"/>);
		UiCritBase.initHeadFromArea(this.critArea, {reg: this.reg}, head);
		head.appendChild(<span class="critTitle">{this.critArea.getLabel(null)}</span>);
		const buttonActions = this.insertAlternativesBtn(init);
		if (buttonActions) head.appendChild(buttonActions);

		if (this.traitsCodeList) {
			const traits = this.reg.getListAsMap(this.traitsCodeList) as Dict<string>;
			if (traits) {
				this._trFilterElt = <select onchange={this.onChange}>
					<option value="" selected>Aucun filtre</option>
				</select> as HTMLSelectElement;
				for (const tr in traits)
					this._trFilterElt.appendChild(<option value={tr}>{traits[tr]}</option>);
				sr.appendChild(<div class="h">
					{this._trFilterElt}
				</div>)
			}
		}

		const optsRoot = sr.appendChild(<div class="h"/>);
		this._opElt = optsRoot.appendChild(<select onchange={this.onChange}>
			<option value="gt" selected>{UiCritSubRequest.OP["gt"][0]}</option>
			<option value="gt=">{UiCritSubRequest.OP["gt="][0]}</option>
			<option value="lt">{UiCritSubRequest.OP["lt"][0]}</option>
			<option value="lt=">{UiCritSubRequest.OP["lt="][0]}</option>
			<option value="=">{UiCritSubRequest.OP["="][0]}</option>
		</select>) as HTMLSelectElement;
		this._opValueElt = optsRoot.appendChild(<input value="0" type="number" min="0" step="1" oninput={this.onChange} onchange={this.onChange}/>) as HTMLInputElement;

		sr.appendChild(<slot id="critCh"/>);
		this._subExpElt = this.appendChild(UiCritBool.AREA_AND.buildBody(this)) as UiCritBool;
		this._subExpElt.initialize(this);
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		const LinkExpXml = UiCritSubRequestArea.findSubExpLinksElement(xml);
		if (xml?.hasAttribute("op")) this._opElt.value = xml.getAttribute("op");
		if (xml?.hasAttribute("value")) this._opValueElt.value = xml.getAttribute("value");
		const linkTraitFilter = LinkExpXml?.getAttribute('linkTraitFilter');
		if (linkTraitFilter && this._trFilterElt)
			this._trFilterElt.value = linkTraitFilter

		if (LinkExpXml?.nextElementSibling)
			await this._subExpElt.initFromXmlCrit(LinkExpXml.nextElementSibling);
		return this;
	}

	async initFromInsAction(xml?: Element): Promise<this> {
		const LinkExpXml = UiCritSubRequestArea.findSubExpLinksElement(xml);
		const linkTraitFilter = LinkExpXml?.getAttribute('linkTraitFilter');
		if (linkTraitFilter && this._trFilterElt)
			this._trFilterElt.value = linkTraitFilter
		return super.initFromInsAction(xml);
	}

	buildCrit(): ISearchCrit | undefined {
		// Critérion
		const subExp = new SearchAnd();
		const crit = new SearchOccurencesSubExp(this._opElt.value as EOperatorsSimple, Number.parseInt(this._opValueElt.value), subExp);

		// Condition sur le subExp
		let searchItemLinks: SearchItemLinks;
		switch (this.critArea) {
		case UiCritSubRequest.AREA_DESC :
			searchItemLinks = new SearchItemLinks("LinkDesc");
			break;
		case UiCritSubRequest.AREA_ASC :
			searchItemLinks = new SearchItemLinks("LinkAsc");
			break;
		case UiCritSubRequest.AREA_PARENTS :
			searchItemLinks = new SearchItemLinks("LinkParents");
			break;
		default :
			// AREA_CHILDREN
			searchItemLinks = new SearchItemLinks("LinkChildren");
		}
		searchItemLinks.setPath(".");
		searchItemLinks.setLinkTraitFilter(this._trFilterElt?.value)
		subExp.push(searchItemLinks);

		// Critérions fils
		LANG.pushIfDefined(subExp, this._subExpElt.buildCrit());

		return crit;
	}

	buildUiXmlCrit(): Element | undefined {
		const crit: Element = super.buildUiXmlCrit();
		crit.setAttribute("uiCrit", "wsp-uicrit-subrequest:" + this.critArea.getId());
		const subExpCritElt = crit.firstElementChild;
		// Supp du bloc de critères "fils" non "UI"
		subExpCritElt.removeChild(subExpCritElt.lastChild);
		// Ajout du bloc de critères "fils" "UI"
		subExpCritElt.appendChild(this._subExpElt.buildUiXmlCrit());
		return crit;
	}
}

customElements.define("wsp-uicrit-subrequest", UiCritSubRequest);

/**
 * UiCritFixedCriterion : critère de recherche basé sur un critérion pré-fixé, sans paramétrage
 */
export class UiCritFixedCriterionArea extends UiCritArea {
	constructor(public readonly criterion: ISearchCrit, libs?: IEndPoint | IEndPoint[], id?: string) {
		super('wsp-uicrit-fixed-criterion', libs, id);
		this.critSgn = "fixed";
		this.replacePattern = /\bstring\b/;
		this.buildAbstract = function (this: UiCritArea, xml: Element, ctx: OUiCritInit) {
			return <span class="crit"><span class="critTitle">{this.getLabel(ctx)}</span></span>;
		};
	}
}

export class UiCritFixedCriterion extends UiCritBase {
	critArea: UiCritFixedCriterionArea;

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;
		sr.appendChild(<div class="critHead">
			<span class="critTitle">{this.critArea.getLabel(init)}</span>
			{this.insertAlternativesBtn(init)}
		</div>);
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		return this.critArea.criterion;
	}

}

customElements.define("wsp-uicrit-fixed-criterion", UiCritFixedCriterion);
